import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  X, Plus, Pencil, Archive, ArchiveRestore, Trash2, Check, Layers,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

const emptyForm = { name: "", description: "", defaultPrice: "", defaultSessionCount: "" };

export default function ProgramsModal({ onClose }) {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/training-programs", { params: { include_archived: true } });
      setPrograms(res.data);
    } catch (err) {
      toast.error("Failed to load programs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const visible = useMemo(
    () => programs.filter((p) => (showArchived ? true : !p.archived)),
    [programs, showArchived]
  );

  function startEdit(p) {
    setEditingId(p.id);
    setForm({
      name: p.name,
      description: p.description || "",
      defaultPrice: p.defaultPrice ? String(p.defaultPrice) : "",
      defaultSessionCount: p.defaultSessionCount ? String(p.defaultSessionCount) : "",
    });
  }

  function resetForm() {
    setEditingId(null);
    setForm(emptyForm);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Program name is required");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        name: form.name.trim(),
        description: form.description.trim(),
        defaultPrice: form.defaultPrice ? Number(form.defaultPrice) : 0,
        defaultSessionCount: form.defaultSessionCount ? parseInt(form.defaultSessionCount, 10) : 0,
      };
      if (editingId) {
        await api.put(`/training-programs/${editingId}`, body);
        toast.success("Program updated");
      } else {
        await api.post("/training-programs", body);
        toast.success("Program created");
      }
      resetForm();
      await refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setSubmitting(false);
    }
  }

  async function toggleArchive(p) {
    try {
      await api.put(`/training-programs/${p.id}`, { archived: !p.archived });
      toast.success(p.archived ? "Program restored" : "Program archived");
      await refresh();
    } catch (err) {
      toast.error("Failed to update program");
    }
  }

  async function remove(p) {
    if (!window.confirm(`Delete "${p.name}"? Existing client assignments will keep the program name as historical data.`)) return;
    try {
      await api.delete(`/training-programs/${p.id}`);
      toast.success("Program deleted");
      if (editingId === p.id) resetForm();
      await refresh();
    } catch (err) {
      toast.error("Failed to delete program");
    }
  }

  return (
    <div
      data-testid="programs-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-3xl bg-white p-6 shadow-xl"
      >
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Layers size={22} />
            <h2 className="text-2xl font-bold">Training Programs</h2>
          </div>
          <Button
            variant="outline"
            onClick={onClose}
            className="rounded-2xl"
            data-testid="programs-close-btn"
          >
            <X size={18} />
          </Button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="mb-5 grid gap-3 rounded-2xl border bg-slate-50 p-4 md:grid-cols-2"
          data-testid="programs-form"
        >
          <label className="space-y-1 text-sm font-medium md:col-span-2">
            Program name
            <input
              data-testid="programs-form-name"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Adolescent Manners"
              className="w-full rounded-xl border bg-white p-2 font-normal"
            />
          </label>
          <label className="space-y-1 text-sm font-medium">
            Default price
            <input
              data-testid="programs-form-price"
              type="number"
              min="0"
              step="0.01"
              value={form.defaultPrice}
              onChange={(e) => setForm({ ...form, defaultPrice: e.target.value })}
              className="w-full rounded-xl border bg-white p-2 font-normal"
            />
          </label>
          <label className="space-y-1 text-sm font-medium">
            Default session count
            <input
              data-testid="programs-form-sessions"
              type="number"
              min="0"
              step="1"
              value={form.defaultSessionCount}
              onChange={(e) => setForm({ ...form, defaultSessionCount: e.target.value })}
              className="w-full rounded-xl border bg-white p-2 font-normal"
            />
          </label>
          <label className="space-y-1 text-sm font-medium md:col-span-2">
            Description
            <textarea
              data-testid="programs-form-description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
              className="w-full rounded-xl border bg-white p-2 font-normal"
            />
          </label>
          <div className="flex gap-2 md:col-span-2">
            <Button
              data-testid="programs-save-btn"
              type="submit"
              disabled={submitting}
              className="rounded-2xl"
            >
              {editingId ? (
                <><Check size={14} className="mr-1.5" />Update program</>
              ) : (
                <><Plus size={14} className="mr-1.5" />Add program</>
              )}
            </Button>
            {editingId && (
              <Button
                type="button"
                variant="outline"
                onClick={resetForm}
                className="rounded-2xl"
                data-testid="programs-cancel-edit-btn"
              >
                Cancel edit
              </Button>
            )}
            <label className="ml-auto flex items-center gap-1.5 text-sm text-slate-600">
              <input
                data-testid="programs-show-archived"
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                className="h-4 w-4"
              />
              Show archived
            </label>
          </div>
        </form>

        {loading ? (
          <p className="text-slate-500">Loading programs…</p>
        ) : visible.length === 0 ? (
          <p className="text-slate-500">No programs yet — add one above.</p>
        ) : (
          <div className="space-y-2" data-testid="programs-list">
            {visible.map((p) => (
              <div
                key={p.id}
                data-testid={`program-row-${p.id}`}
                className={`flex flex-col gap-2 rounded-2xl border p-3 md:flex-row md:items-center md:justify-between ${
                  p.archived ? "bg-slate-50 opacity-70" : ""
                }`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold">{p.name}</p>
                    {p.archived && (
                      <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs text-slate-700">
                        Archived
                      </span>
                    )}
                    <span className="text-xs text-slate-500">
                      {p.defaultPrice > 0 ? `$${p.defaultPrice.toFixed(2)}` : "No price set"}
                      {p.defaultSessionCount > 0 ? ` • ${p.defaultSessionCount} sessions` : ""}
                    </span>
                  </div>
                  {p.description && (
                    <p className="mt-0.5 text-sm text-slate-600">{p.description}</p>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => startEdit(p)}
                    className="rounded-xl"
                    data-testid={`program-edit-${p.id}`}
                  >
                    <Pencil size={13} className="mr-1" />Edit
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => toggleArchive(p)}
                    className="rounded-xl"
                    data-testid={`program-archive-${p.id}`}
                  >
                    {p.archived ? (
                      <><ArchiveRestore size={13} className="mr-1" />Restore</>
                    ) : (
                      <><Archive size={13} className="mr-1" />Archive</>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => remove(p)}
                    className="rounded-xl text-rose-600 hover:bg-rose-50"
                    data-testid={`program-delete-${p.id}`}
                  >
                    <Trash2 size={13} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="mt-4 text-xs text-slate-500">
          Programs are shared across your workspace. Existing clients keep their program name as
          historical data even if you later delete a program.
        </p>
      </motion.div>
    </div>
  );
}
